import { motion } from "framer-motion";

// Toolkit — Klein blue tiles to match the brand. Matches reference logo strip.
const KLEIN = "#0851F2";
const tools = [
  { name: "Figma", abbr: "F", note: "Daily" },
  { name: "Photoshop", abbr: "Ps", note: "Daily" },
  { name: "Illustrator", abbr: "Ai", note: "Daily" },
  { name: "InDesign", abbr: "Id", note: "Often" },
  { name: "Blender", abbr: "Bl", note: "Used" },
  { name: "Premiere Pro", abbr: "Pr", note: "Often" },
  { name: "CapCut", abbr: "Cc", note: "Used" },
  { name: "Canva", abbr: "C", note: "Used" },
];

function ToolTile({ tool }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -4 }}
      className="flex flex-col items-center gap-3"
      data-testid={`tool-${tool.name.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div
        className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center font-sans-display font-bold text-2xl md:text-3xl tabular-nums shadow-[0_8px_24px_-12px_rgba(8,81,242,0.45)] transition-shadow duration-300"
        style={{
          background: KLEIN,
          color: "#FFFFFF",
          boxShadow: `inset 0 0 0 2px #FFFFFF, 0 8px 24px -12px rgba(8,81,242,0.6)`,
        }}
      >
        {tool.abbr}
      </div>
      <div className="text-center">
        <div className="font-sans-display uppercase text-sm md:text-base leading-none tracking-[0.01em]">
          {tool.name}
        </div>
        <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
          {tool.note}
        </div>
      </div>
    </motion.div>
  );
}

export default function About() {
  return (
    <section
      id="about"
      className="relative py-24 md:py-40 bg-white overflow-hidden"
      data-testid="about-section"
    >
      {/* Soft gradient strip blending Graphic Design → About → cream paper feel */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-40"
        style={{
          background:
            "linear-gradient(to bottom, rgba(8,81,242,0.04), rgba(255,255,255,0))",
        }}
      />

      {/* Subtle Klein-blue accent shapes — pushed to the side margins so content reads cleanly */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {/* LEFT margin */}
        <svg
          className="absolute"
          style={{ left: "-90px", top: "14%", width: "280px", height: "280px", opacity: 0.22 }}
          viewBox="0 0 280 280"
          fill="none"
        >
          <circle cx="140" cy="140" r="130" stroke="#0851F2" strokeWidth="3" />
        </svg>
        <svg
          className="absolute"
          style={{ left: "1%", bottom: "10%", width: "180px", height: "160px", opacity: 0.3 }}
          viewBox="0 0 180 160"
          fill="none"
        >
          <polygon points="90,10 170,150 10,150" stroke="#0851F2" strokeWidth="3" />
        </svg>
        <span
          className="absolute"
          style={{
            left: "2%",
            top: "55%",
            width: "70px",
            height: "70px",
            background: "#0851F2",
            opacity: 0.18,
            transform: "rotate(-12deg)",
          }}
        />

        {/* RIGHT margin */}
        <svg
          className="absolute"
          style={{ right: "1%", top: "10%", width: "240px", height: "60px", opacity: 0.4 }}
          viewBox="0 0 240 60"
          fill="none"
        >
          <path
            d="M 5 30 Q 35 5, 65 30 T 125 30 T 185 30 T 235 30"
            stroke="#0851F2"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
        <span
          className="absolute"
          style={{
            right: "3%",
            top: "32%",
            width: "120px",
            height: "120px",
            background: "#0851F2",
            opacity: 0.14,
            transform: "rotate(14deg)",
          }}
        />
        <svg
          className="absolute"
          style={{ right: "-60px", bottom: "8%", width: "240px", height: "240px", opacity: 0.22 }}
          viewBox="0 0 240 240"
          fill="none"
        >
          <circle cx="120" cy="120" r="110" stroke="#0851F2" strokeWidth="2.5" />
          <circle cx="120" cy="120" r="70" stroke="#0851F2" strokeWidth="2.5" />
          <circle cx="120" cy="120" r="32" stroke="#0851F2" strokeWidth="2.5" />
        </svg>
        <span
          className="absolute"
          style={{
            right: "2%",
            top: "62%",
            width: "32px",
            height: "32px",
            background:
              "linear-gradient(#0851F2,#0851F2) center/100% 4px no-repeat," +
              "linear-gradient(#0851F2,#0851F2) center/4px 100% no-repeat",
            opacity: 0.6,
          }}
        />
      </div>

      {/* Giant EMILLY watermark — sits behind content */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-x-0 -top-6 md:-top-10 flex justify-center"
      >
        <span
          className="font-sans-display uppercase leading-[0.85] tracking-[-0.04em]"
          style={{
            fontSize: "clamp(140px, 22vw, 360px)",
            color: "#0851F2",
            opacity: 0.16,
            whiteSpace: "nowrap",
          }}
        >
          EMILLY
        </span>
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* Left — portrait with bold graphic doodles */}
          <div className="md:col-span-5 md:sticky md:top-32 self-start">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="relative h-[520px] md:h-[640px]"
              data-testid="about-portrait"
            >
              {/* Background bold shapes */}
              <span
                aria-hidden="true"
                className="absolute -top-6 -left-6 w-44 h-44 rounded-full"
                style={{ background: "#0851F2" }}
              />
              <span
                aria-hidden="true"
                className="absolute -bottom-6 -right-4 w-36 h-36"
                style={{ background: "#FFC72C" }}
              />
              <span
                aria-hidden="true"
                className="absolute top-1/3 -right-10 w-24 h-24 rotate-12"
                style={{ background: "#E63946" }}
              />

              {/* Portrait frame */}
              <div className="relative w-full h-full overflow-hidden border-[3px] border-[var(--ink)] shadow-[10px_12px_0_var(--ink)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/tii0xkmj_AD1FC00C-E69C-4EA9-B2F3-2857181DE555_SnapseedCopy.jpeg"
                  alt="Emilly Nešković — portrait"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "60% 55%" }}
                />
              </div>

              {/* Hand-drawn squiggles & scribbles on top */}
              <svg
                aria-hidden="true"
                viewBox="0 0 400 600"
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full pointer-events-none"
              >
                {/* Star/sparkle top-right */}
                <g transform="translate(330,60)">
                  <path
                    d="M0 -22 L6 -6 L22 0 L6 6 L0 22 L-6 6 L-22 0 L-6 -6 Z"
                    fill="#FFC72C"
                    stroke="#0a0a0a"
                    strokeWidth="3"
                    strokeLinejoin="round"
                  />
                </g>
                {/* Squiggly line */}
                <path
                  d="M 30 540 q 20 -30 40 0 t 40 0 t 40 0 t 40 0"
                  fill="none"
                  stroke="#0851F2"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
                {/* Bold arrow pointing at her */}
                <g transform="translate(20,200) rotate(-12)">
                  <path
                    d="M 0 0 L 80 -8"
                    stroke="#E63946"
                    strokeWidth="6"
                    strokeLinecap="round"
                    fill="none"
                  />
                  <path
                    d="M 80 -8 L 64 -22 L 80 -8 L 64 6"
                    stroke="#E63946"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </g>
                {/* Hand-drawn label */}
                <text
                  x="40"
                  y="180"
                  fontFamily="Shrikhand, cursive"
                  fontSize="22"
                  fill="#0a0a0a"
                  transform="rotate(-8, 40, 180)"
                >
                  hi !
                </text>
                {/* Small heart bottom-left */}
                <path
                  d="M 60 500 q -10 -14 -22 -2 q -6 12 22 28 q 28 -16 22 -28 q -12 -12 -22 2 Z"
                  fill="#E63946"
                  stroke="#0a0a0a"
                  strokeWidth="3"
                  strokeLinejoin="round"
                />
                {/* Spiral top-left */}
                <path
                  d="M 50 70 c 14 -16 30 -2 18 12 c -12 14 -28 -2 -10 -18 c 18 -16 38 4 22 22"
                  fill="none"
                  stroke="#0851F2"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                {/* Tiny dots */}
                <circle cx="370" cy="280" r="6" fill="#0851F2" />
                <circle cx="20" cy="380" r="5" fill="#FFC72C" />
                <circle cx="380" cy="450" r="7" fill="#E63946" />
              </svg>

              {/* Caption strip */}
              <div className="absolute bottom-[-40px] left-2 right-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink)]">
                <span>[ Emilly · UI/UX Designer ]</span>
                <span>Belgrade, RS</span>
              </div>
            </motion.div>
          </div>

          {/* Right — narrative */}
          <div className="md:col-span-7">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              §04 — About
            </span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mt-6 font-sans-display text-4xl md:text-6xl leading-[1] tracking-[-0.03em] font-light uppercase"
            >
              Hi, I'm{" "}
              <span
                className="font-rarefig normal-case"
                style={{
                  color: "transparent",
                  WebkitTextStroke: "1.5px #0851F2",
                  letterSpacing: "0.01em",
                }}
              >
                Emilly.
              </span>
            </motion.h2>

            <div className="mt-10 space-y-6 text-lg md:text-xl leading-relaxed text-[var(--ink-soft)] max-w-[58ch]">
              <p>
                A UI/UX Designer with a background in multimedia and digital
                design. I graduated from{" "}
                <span className="text-[var(--ink)]">
                  The Academy of Applied Studies — Polytechnic
                </span>{" "}
                with a{" "}
                <span className="text-[var(--ink)] font-semibold">
                  degree in UI/UX Design
                </span>
                , and previously earned a qualification as a Multimedia
                Electrical Technician.
              </p>
              <p>
                I'm passionate about creating bold, engaging and user-centered
                digital experiences. My approach combines creativity,
                functionality and attention to detail — often incorporating
                playful elements that make products feel more memorable and
                enjoyable.
              </p>
              <p>
                Beyond digital design, I have a strong appreciation for{" "}
                <span className="text-[var(--ink)] font-semibold">
                  art and visual storytelling
                </span>
                . In my free time, I enjoy oil painting on canvas, which
                inspires my creative thinking and helps me explore new
                perspectives in design. I'm also a passionate video game
                enthusiast — games have shaped the way I think about
                interaction, worlds and the little moments of delight hidden
                inside an interface.
              </p>
              <p>
                I continuously explore new design trends, technologies and
                creative techniques to improve my skills and deliver meaningful
                user experiences.
              </p>
            </div>
          </div>
        </div>

        {/* Toolkit — full-width below */}
        <div className="mt-20 md:mt-28 pt-12 md:pt-16 border-t border-[var(--line)]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
                §04.a — Toolkit
              </span>
              <h3 className="mt-4 font-sans-display text-3xl md:text-5xl font-light uppercase tracking-[-0.02em]">
                Software &{" "}
                <span className="font-display italic lowercase">tools</span>
              </h3>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)] max-w-[40ch]">
              The kit I reach for every day — design, illustration, layout and
              motion.
            </span>
          </div>

          <div className="grid grid-cols-4 md:grid-cols-8 gap-6 md:gap-8">
            {tools.map((t) => (
              <ToolTile key={t.name} tool={t} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
